import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLong } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { ButtonStandard } from '../../button/ButtonStandard/ButtonStandard';
import { useScreenSm } from '../../hooks/mediaQueries/useScreenSm';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalOpened } from '../../hooks/useModalOpened/useModalOpened';
import { SelectLocationDialog } from '../../location/SelectLocationDialog/SelectLocationDialog';
import { IconDeprecated, IconType } from '../../misc/IconDeprecated/IconDeprecated';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { InputLayout, InputLayoutProps } from '../InputLayout/InputLayout';
import sharedStyles from '../sharedStyles.module.css';
import styles from './LocationInput.module.css';
export type LocationInputProps = PropsWithoutChildren<Omit<InputLayoutProps, 'children' | 'onInput' | 'onChange' | 'onInputCapture' | 'onChangeCapture'>> & {
  value: LatLong | null;
  onValueChanged: (value: LatLong | null) => void;
};
export const LocationInput: FunctionComponent<LocationInputProps> = React.memo(props => {
  const $ = _c(32);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let onValueChanged;
  let remainingProps;
  let value;
  if ($[2] !== t0) {
    ({
      value,
      onValueChanged,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = onValueChanged;
    $[4] = remainingProps;
    $[5] = value;
  } else {
    onValueChanged = $[3];
    remainingProps = $[4];
    value = $[5];
  }
  const {
    t
  } = useTranslation("record-it-components");
  const screenSm = useScreenSm();
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      closeOnBackDropClick: false
    };
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    close: closeSelectLocationDialog,
    open: openSelectLocationDialog,
    modalProps: selectLocationDialogModalProps
  } = useModalOpened(t1);
  let t2;
  if ($[7] !== closeSelectLocationDialog || $[8] !== onValueChanged) {
    t2 = location => {
      onValueChanged(location);
      closeSelectLocationDialog();
    };
    $[7] = closeSelectLocationDialog;
    $[8] = onValueChanged;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleAcceptClick = useEventCallback(t2);
  let t3;
  bb0: {
    if (!value) {
      let t4;
      if ($[10] !== t) {
        t4 = t("input.LocationInput.noValue");
        $[10] = t;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t3 = t4;
      break bb0;
    }
    const digits = screenSm ? 6 : 2;
    let t4;
    if ($[12] !== digits || $[13] !== value.latitude) {
      t4 = value.latitude.toFixed(digits);
      $[12] = digits;
      $[13] = value.latitude;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== digits || $[16] !== value.longitude) {
      t5 = value.longitude.toFixed(digits);
      $[15] = digits;
      $[16] = value.longitude;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t3 = `${t4} / ${t5}`;
  }
  const valueText = t3;
  let t4;
  if ($[18] !== openSelectLocationDialog || $[19] !== valueText) {
    t4 = () => <div className={styles.contentWrapper}><input className={classes(sharedStyles.inputBaseStyle, styles.input)} value={valueText} size={valueText.length} readOnly={true} onClick={openSelectLocationDialog} data-testid="LocationInput.Input" /><ButtonStandard className={styles.selectLocationInDialogButton} onClick={openSelectLocationDialog} data-testid="LocationInput.SelectLocationButton"><IconDeprecated name="map-location-dot" type={IconType.Regular} className={styles.icon} /></ButtonStandard></div>;
    $[18] = openSelectLocationDialog;
    $[19] = valueText;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== remainingProps || $[22] !== t4) {
    t5 = <InputLayout {...remainingProps}>{t4}</InputLayout>;
    $[21] = remainingProps;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== closeSelectLocationDialog || $[25] !== handleAcceptClick || $[26] !== selectLocationDialogModalProps || $[27] !== value) {
    t6 = <SelectLocationDialog {...selectLocationDialogModalProps} location={value} onCancel={closeSelectLocationDialog} onAccept={handleAcceptClick} data-testid="LocationInput.SelectLocationDialog" />;
    $[24] = closeSelectLocationDialog;
    $[25] = handleAcceptClick;
    $[26] = selectLocationDialogModalProps;
    $[27] = value;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t5 || $[30] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
});